/** How a database's factors are expressed. */
export const FACTOR_BASES = Object.freeze([
  { value: "PHYSICAL", label: "Physical", hint: "per kWh, kg, km…" },
  { value: "SPEND", label: "Spend", hint: "per unit of money, at a price year" },
]);

export const LINK_FILTERS = Object.freeze([
  { value: "", label: "Any link" },
  { value: "with", label: "With source link" },
  { value: "without", label: "Without source link" },
]);

export const SORTS = Object.freeze([
  { value: "priority", label: "Priority" },
  { value: "name", label: "Name" },
  { value: "factors", label: "Most factors" },
]);

export const MAX_PRIORITY = 999;

/**
 * @typedef {object} GhgDatabase
 * @property {number} id
 * @property {string} name
 * @property {string|null} source
 * @property {number|null} priority_order lower is applied first
 * @property {"PHYSICAL"|"SPEND"} factor_basis
 * @property {number|null} price_year
 * @property {number} factor_count
 * @property {{ id: number, title: string, url: string|null, note: string|null }|null} reference
 */

/**
 * @typedef {object} DatabaseFilters
 * @property {string} search matched against name and source label
 * @property {string} basis "" for any
 * @property {string} link "", "with" or "without"
 * @property {string} sort one of SORTS
 */

export const EMPTY_FILTERS = Object.freeze({ search: "", basis: "", link: "", sort: "priority" });

/** @param {GhgDatabase} database @returns {number} */
const priorityOf = (database) => database.priority_order ?? MAX_PRIORITY;

const COMPARE = {
  priority: (a, b) => priorityOf(a) - priorityOf(b) || a.name.localeCompare(b.name),
  name: (a, b) => a.name.localeCompare(b.name),
  factors: (a, b) => b.factor_count - a.factor_count || a.name.localeCompare(b.name),
};

/**
 * The databases the filters keep, in the chosen order.
 *
 * @param {GhgDatabase[]} databases
 * @param {DatabaseFilters} filters
 * @returns {GhgDatabase[]}
 */
export const filterDatabases = (databases, { search, basis, link, sort }) => {
  const needle = search.trim().toLowerCase();
  return databases
    .filter((database) => !needle || `${database.name} ${database.source || ""}`.toLowerCase().includes(needle))
    .filter((database) => !basis || database.factor_basis === basis)
    .filter((database) => !link || (link === "with") === Boolean(database.reference?.url))
    .sort(COMPARE[sort] || COMPARE.priority);
};

/**
 * @param {GhgDatabase[]} databases
 * @returns {{ total: number, withFactors: number, withLink: number, spend: number }}
 */
export const summarizeDatabases = (databases) => ({
  total: databases.length,
  withFactors: databases.filter((database) => database.factor_count > 0).length,
  withLink: databases.filter((database) => database.reference?.url).length,
  spend: databases.filter((database) => database.factor_basis === "SPEND").length,
});

/**
 * @typedef {object} DatabaseDraft
 * @property {number|null} id null for a new database
 * @property {string} name
 * @property {string} source
 * @property {string} priority
 * @property {"PHYSICAL"|"SPEND"} basis
 * @property {string} priceYear
 * @property {string} referenceId "" for none
 */

/** @returns {DatabaseDraft} */
export const emptyDraft = () => ({ id: null, name: "", source: "", priority: "", basis: "PHYSICAL", priceYear: "", referenceId: "" });

/**
 * @param {GhgDatabase} database
 * @returns {DatabaseDraft}
 */
export const draftFromDatabase = (database) => ({
  id: database.id,
  name: database.name,
  source: database.source || "",
  priority: database.priority_order == null ? "" : String(database.priority_order),
  basis: database.factor_basis,
  priceYear: database.price_year == null ? "" : String(database.price_year),
  referenceId: database.reference ? String(database.reference.id) : "",
});

/**
 * The same rules the backend applies, plus a name already used by another
 * database, so a bad draft is caught in the form.
 *
 * @param {DatabaseDraft} draft
 * @param {GhgDatabase[]} databases
 * @returns {string} what is wrong, or "" when it can be saved
 */
export const draftError = (draft, databases) => {
  const name = draft.name.trim();
  if (!name) return "Name is required";
  if (databases.some((database) => database.id !== draft.id && database.name.trim().toLowerCase() === name.toLowerCase())) {
    return `A database named "${name}" already exists`;
  }
  const priority = draft.priority.trim();
  if (priority && !(/^\d+$/.test(priority) && Number(priority) >= 1 && Number(priority) <= MAX_PRIORITY)) {
    return `Priority must be a whole number from 1 to ${MAX_PRIORITY}`;
  }
  if (draft.basis === "SPEND") {
    const year = Number(draft.priceYear);
    if (!draft.priceYear.trim()) return "A spend database needs the year its prices are in";
    if (!Number.isInteger(year) || year < 1990 || year > 2100) return "Price year must be between 1990 and 2100";
  }
  return "";
};

/**
 * @param {DatabaseDraft} draft
 * @returns {{ name: string, source: string, priority_order: number|null, factor_basis: string, price_year: number|null, reference_id: number|null }}
 */
export const draftToPayload = (draft) => ({
  name: draft.name.trim(),
  source: draft.source.trim(),
  priority_order: draft.priority.trim() ? Number(draft.priority) : null,
  factor_basis: draft.basis,
  price_year: draft.basis === "SPEND" ? Number(draft.priceYear) : null,
  reference_id: draft.referenceId ? Number(draft.referenceId) : null,
});
